// User Management Page - Admin CRUD for users, roles, scopes, and password resets

import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, Button, StatusBadge, Modal } from '../components/common';
import { formatDate, formatRelativeTime } from '../utils/dateFormatters';
import { userManagementService } from '../services/userManagement';
import type { UserListItem, UserDetail, ScopeInfo, RoleInfo } from '../services/userManagement';
import { PlusIcon, RefreshIcon } from '../components/icons';
import styles from './UserManagementPage.module.css';

// Icon for Users (inline SVG, zero-dep)
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

type ModalMode = 'create' | 'edit' | 'resetPassword' | null;

export function UserManagementPage() {
  // Data state
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [showInactive, setShowInactive] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Create form
  const [createEmail, setCreateEmail] = useState('');
  const [createName, setCreateName] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createRole, setCreateRole] = useState('user');
  const [createSalespersonId, setCreateSalespersonId] = useState('');

  // Edit form
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editSalespersonId, setEditSalespersonId] = useState('');
  const [editScopeAdds, setEditScopeAdds] = useState<Set<string>>(new Set());
  const [editScopeRemoves, setEditScopeRemoves] = useState<Set<string>>(new Set());

  // Password reset form
  const [newPassword, setNewPassword] = useState('');
  // Set-password link (kanban c369e94b): the person chooses their own password
  const [lockCurrentPassword, setLockCurrentPassword] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userManagementService.list({
        include_inactive: showInactive,
        role: roleFilter || undefined,
        search: searchTerm || undefined,
      });
      setUsers(data.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [showInactive, roleFilter, searchTerm]);

  const fetchMetadata = useCallback(async () => {
    try {
      const [scopeData, roleData] = await Promise.all([
        userManagementService.listScopes(),
        userManagementService.listRoles(),
      ]);
      setScopes(scopeData);
      setRoles(roleData);
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchMetadata(); }, [fetchMetadata]);

  // === Handlers ===

  const openCreateModal = () => {
    setModalMode('create');
    setCreateEmail('');
    setCreateName('');
    setCreatePassword('');
    setCreateRole('user');
    setCreateSalespersonId('');
    setFormError(null);
    setFormSuccess(null);
  };

  const openEditModal = async (userId: string) => {
    setActionLoading(userId);
    setFormError(null);
    setFormSuccess(null);
    try {
      const detail = await userManagementService.get(userId);
      setSelectedUser(detail);
      setEditName(detail.name);
      setEditEmail(detail.email);
      setEditRole(detail.role);
      setEditStatus(detail.status);
      setEditSalespersonId(detail.salesperson_id || '');
      setEditScopeAdds(new Set(detail.scope_overrides?.add || []));
      setEditScopeRemoves(new Set(detail.scope_overrides?.remove || []));
      setModalMode('edit');
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const openResetPasswordModal = async (userId: string) => {
    setActionLoading(userId);
    setFormError(null);
    setFormSuccess(null);
    try {
      const detail = await userManagementService.get(userId);
      setSelectedUser(detail);
      setNewPassword('');
      setLockCurrentPassword(false);
      setModalMode('resetPassword');
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreate = async () => {
    if (!createName.trim() || !createEmail.trim() || !createPassword) {
      setFormError('Name, email, and password are required');
      return;
    }
    if (createPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    setActionLoading('create');
    setFormError(null);
    try {
      await userManagementService.create({
        email: createEmail.trim(),
        name: createName.trim(),
        password: createPassword,
        role: createRole,
        salesperson_id: createSalespersonId.trim() || undefined,
      });
      setModalMode(null);
      await fetchUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFormError(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;

    setActionLoading('update');
    setFormError(null);
    try {
      const scopeOverrides: { add?: string[]; remove?: string[] } = {};
      if (editScopeAdds.size > 0) scopeOverrides.add = [...editScopeAdds];
      if (editScopeRemoves.size > 0) scopeOverrides.remove = [...editScopeRemoves];

      await userManagementService.update(selectedUser.id, {
        name: editName.trim() || undefined,
        email: editEmail.trim() || undefined,
        role: editRole || undefined,
        status: editStatus || undefined,
        salesperson_id: editSalespersonId.trim() || null,
        scope_overrides: (editScopeAdds.size > 0 || editScopeRemoves.size > 0) ? scopeOverrides : undefined,
      });
      setModalMode(null);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFormError(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (!newPassword || newPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    setActionLoading('reset');
    setFormError(null);
    try {
      const result = await userManagementService.resetPassword(selectedUser.id, {
        new_password: newPassword,
      });
      setFormSuccess(result.message);
      setNewPassword('');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFormError(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendSetPasswordLink = async () => {
    if (!selectedUser) return;
    setActionLoading('sendLink');
    setFormError(null);
    try {
      const result = await userManagementService.sendSetPasswordLink(selectedUser.id, {
        lock_current_password: lockCurrentPassword,
      });
      if (result.status === 'sent') {
        setFormSuccess(result.message);
      } else {
        setFormError(result.message);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFormError(error.response?.data?.detail || 'Failed to send the set-password link');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (user: UserListItem) => {
    if (!confirm(`Deactivate user "${user.name}" (${user.email})? They will no longer be able to log in.`)) return;

    setActionLoading(user.id);
    try {
      await userManagementService.deactivate(user.id);
      await fetchUsers();
    } catch (err) {
      console.error('Failed to deactivate user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (user: UserListItem) => {
    setActionLoading(user.id);
    try {
      await userManagementService.reactivate(user.id);
      await fetchUsers();
    } catch (err) {
      console.error('Failed to reactivate user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Scope toggle helpers
  const getDefaultScopesForRole = (role: string): string[] => {
    const r = roles.find(r => r.role === role.toLowerCase());
    return r?.default_scopes || [];
  };

  const toggleScopeAdd = (scope: string) => {
    setEditScopeAdds(prev => {
      const next = new Set(prev);
      if (next.has(scope)) { next.delete(scope); } else { next.add(scope); }
      return next;
    });
    // Can't both add and remove
    setEditScopeRemoves(prev => {
      const next = new Set(prev);
      next.delete(scope);
      return next;
    });
  };

  const toggleScopeRemove = (scope: string) => {
    setEditScopeRemoves(prev => {
      const next = new Set(prev);
      if (next.has(scope)) { next.delete(scope); } else { next.add(scope); }
      return next;
    });
    // Can't both add and remove
    setEditScopeAdds(prev => {
      const next = new Set(prev);
      next.delete(scope);
      return next;
    });
  };

  // Stats
  const activeCount = users.filter(u => u.status === 'active').length;
  const inactiveCount = users.filter(u => u.status !== 'active').length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const salespersonCount = users.filter(u => u.role === 'salesperson').length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <UsersIcon className={styles.titleIcon} />
            User Management
          </h1>
          <span className={styles.subtitle}>
            Manage user accounts, roles, and permissions
          </span>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline" onClick={fetchUsers} disabled={loading}>
            <RefreshIcon />
            Refresh
          </Button>
          <Button variant="primary" onClick={openCreateModal}>
            <PlusIcon />
            Create User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{users.length}</div>
            <div className={styles.statLabel}>Total Users</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.active}`}>
          <CardBody>
            <div className={styles.statValue}>{activeCount}</div>
            <div className={styles.statLabel}>Active</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.inactive}`}>
          <CardBody>
            <div className={styles.statValue}>{inactiveCount}</div>
            <div className={styles.statLabel}>Inactive</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.admins}`}>
          <CardBody>
            <div className={styles.statValue}>{adminCount}</div>
            <div className={styles.statLabel}>Admins</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.salespeople}`}>
          <CardBody>
            <div className={styles.statValue}>{salespersonCount}</div>
            <div className={styles.statLabel}>Salespeople</div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show Inactive
        </label>
        <select
          className={styles.filterSelect}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="user">User</option>
          <option value="readonly">Read Only</option>
          <option value="salesperson">Salesperson</option>
        </select>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Users Table */}
      <Card className={styles.tableCard}>
        <CardBody className={styles.tableBody}>
          {loading ? (
            <div className={styles.loading}>Loading users...</div>
          ) : users.length === 0 ? (
            <div className={styles.empty}>
              <UsersIcon className={styles.emptyIcon} />
              <p>No users found</p>
              <p className={styles.emptyHint}>
                {showInactive ? 'No users match your filters' : 'Try enabling "Show Inactive" or adjust your search'}
              </p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={`${user.status === 'inactive' ? styles.inactive : ''} ${user.status === 'suspended' ? styles.suspended : ''}`}
                  >
                    <td>
                      <div className={styles.userName}>{user.name}</div>
                      <div className={styles.userEmail}>{user.email}</div>
                      {user.salesperson_id && (
                        <div className={styles.salespersonId}>SP: {user.salesperson_id}</div>
                      )}
                    </td>
                    <td>
                      <span className={`${styles.roleBadge} ${styles[user.role.toLowerCase()]}`}>
                        {user.role.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <StatusBadge variant={
                        user.status === 'active' ? 'success' :
                        user.status === 'suspended' ? 'error' : 'warning'
                      }>
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </StatusBadge>
                    </td>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #6c757d)' }}>
                      {user.auth_provider}
                    </td>
                    <td style={{ fontSize: '0.8125rem' }}>
                      {formatRelativeTime(user.last_login_at)}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => openEditModal(user.id)}
                          disabled={actionLoading === user.id}
                        >
                          Edit
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => openResetPasswordModal(user.id)}
                          disabled={actionLoading === user.id}
                        >
                          Reset PW
                        </button>
                        {user.status === 'active' ? (
                          <button
                            className={`${styles.actionBtn} ${styles.danger}`}
                            onClick={() => handleDeactivate(user)}
                            disabled={actionLoading === user.id}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleReactivate(user)}
                            disabled={actionLoading === user.id}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Create User Modal */}
      <Modal
        isOpen={modalMode === 'create'}
        onClose={() => setModalMode(null)}
        title="Create New User"
        size="md"
      >
        <div className={styles.modalForm}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Name *</label>
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Full name"
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Email *</label>
              <input
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder="user@chemmasters.net"
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Password *</label>
              <input
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="Min 8 characters"
                className={styles.input}
              />
              <span className={styles.passwordHint}>At least 8 characters</span>
            </div>
            <div className={styles.formGroup}>
              <label>Role *</label>
              <select
                value={createRole}
                onChange={(e) => setCreateRole(e.target.value)}
                className={styles.select}
              >
                <option value="admin">ADMIN</option>
                <option value="manager">MANAGER</option>
                <option value="user">USER</option>
                <option value="readonly">READONLY</option>
                <option value="salesperson">SALESPERSON</option>
              </select>
            </div>
          </div>

          {createRole === 'salesperson' && (
            <div className={styles.formGroup}>
              <label>Salesperson ID <span className={styles.hint}>(required for salesperson role)</span></label>
              <input
                type="text"
                value={createSalespersonId}
                onChange={(e) => setCreateSalespersonId(e.target.value)}
                placeholder="e.g., A_Forbeck"
                className={styles.input}
              />
            </div>
          )}

          {formError && <div className={styles.error}>{formError}</div>}

          <div className={styles.formActions}>
            <Button variant="outline" onClick={() => setModalMode(null)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={actionLoading === 'create'}
            >
              {actionLoading === 'create' ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={modalMode === 'edit'}
        onClose={() => { setModalMode(null); setSelectedUser(null); }}
        title={selectedUser ? `Edit: ${selectedUser.name}` : 'Edit User'}
        size="lg"
      >
        {selectedUser && (
          <div className={styles.modalForm}>
            <div className={styles.userMeta}>
              <span>ID: <strong>{selectedUser.id.slice(0, 8)}...</strong></span>
              <span>Created: <strong>{formatDate(selectedUser.created_at)}</strong></span>
              <span>Last Login: <strong>{selectedUser.last_login_at ? formatRelativeTime(selectedUser.last_login_at) : 'Never'}</strong></span>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Role</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className={styles.select}
                >
                  <option value="admin">ADMIN</option>
                  <option value="manager">MANAGER</option>
                  <option value="user">USER</option>
                  <option value="readonly">READONLY</option>
                  <option value="salesperson">SALESPERSON</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className={styles.select}
                >
                  <option value="active">ACTIVE</option>
                  <option value="inactive">INACTIVE</option>
                  <option value="suspended">SUSPENDED</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Salesperson ID <span className={styles.hint}>(optional)</span></label>
              <input
                type="text"
                value={editSalespersonId}
                onChange={(e) => setEditSalespersonId(e.target.value)}
                placeholder="Leave empty if not a salesperson"
                className={styles.input}
              />
            </div>

            {/* Scope Overrides */}
            <div className={styles.scopeSection}>
              <h4>Scope Overrides <span className={styles.hint}>(customize permissions beyond role defaults)</span></h4>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', margin: '0 0 0.75rem' }}>
                Default scopes for <strong>{editRole.toUpperCase()}</strong>: {getDefaultScopesForRole(editRole).join(', ') || 'none'}
              </p>
              <div className={styles.scopeGrid}>
                {scopes.map(s => {
                  const isDefault = getDefaultScopesForRole(editRole).includes(s.scope);
                  const isAdded = editScopeAdds.has(s.scope);
                  const isRemoved = editScopeRemoves.has(s.scope);

                  return (
                    <div key={s.scope} className={styles.scopeItem}>
                      {isDefault ? (
                        // Default scope — can be removed
                        <input
                          type="checkbox"
                          checked={!isRemoved}
                          onChange={() => toggleScopeRemove(s.scope)}
                          title={isRemoved ? 'Click to restore this default scope' : 'Click to remove this default scope'}
                        />
                      ) : (
                        // Non-default scope — can be added
                        <input
                          type="checkbox"
                          checked={isAdded}
                          onChange={() => toggleScopeAdd(s.scope)}
                          title={isAdded ? 'Click to remove this added scope' : 'Click to add this scope'}
                        />
                      )}
                      <label
                        className={isAdded ? styles.added : isRemoved ? styles.removed : ''}
                        title={s.description}
                      >
                        {s.scope}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {formError && <div className={styles.error}>{formError}</div>}

            <div className={styles.formActions}>
              <Button variant="outline" onClick={() => { setModalMode(null); setSelectedUser(null); }}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleUpdate}
                disabled={actionLoading === 'update'}
              >
                {actionLoading === 'update' ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={modalMode === 'resetPassword'}
        onClose={() => { setModalMode(null); setSelectedUser(null); }}
        title={selectedUser ? `Reset Password: ${selectedUser.name}` : 'Reset Password'}
        size="sm"
      >
        {selectedUser && (
          <div className={styles.modalForm}>
            <div className={styles.userMeta}>
              <span>Email: <strong>{selectedUser.email}</strong></span>
              <span>Role: <strong>{selectedUser.role.toUpperCase()}</strong></span>
            </div>

            {!formSuccess && (
              <div className={styles.linkOption} data-section="set_password_link">
                <strong>Email them a link to choose their own password</strong>
                <span className={styles.passwordHint}>
                  Recommended. The link works once and expires in a day; nobody but them ever sees the password.
                </span>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={lockCurrentPassword}
                    onChange={(e) => setLockCurrentPassword(e.target.checked)}
                    data-action="set_password_link_lock_current"
                    data-target-user-id={selectedUser.id}
                  />
                  Lock the current password right away (they must use the link)
                </label>
                <Button
                  variant="primary"
                  onClick={handleSendSetPasswordLink}
                  disabled={actionLoading === 'sendLink'}
                  data-action="set_password_link_send"
                  data-target-user-id={selectedUser.id}
                >
                  {actionLoading === 'sendLink' ? 'Sending...' : 'Email set-password link'}
                </Button>
              </div>
            )}

            {!formSuccess && <div className={styles.orDivider}>or type a password for them</div>}

            {!formSuccess && (
              <div className={styles.formGroup}>
                <label>New Password *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className={styles.input}
                />
                <span className={styles.passwordHint}>At least 8 characters. Never send it by email; use the link above instead.</span>
              </div>
            )}

            {formError && <div className={styles.error}>{formError}</div>}
            {formSuccess && <div className={styles.success}>{formSuccess}</div>}

            <div className={styles.formActions}>
              <Button variant="outline" onClick={() => { setModalMode(null); setSelectedUser(null); }}>
                {formSuccess ? 'Done' : 'Cancel'}
              </Button>
              {!formSuccess && (
                <Button
                  variant="outline"
                  onClick={handleResetPassword}
                  disabled={actionLoading === 'reset'}
                  data-action="reset_password_typed"
                  data-target-user-id={selectedUser.id}
                >
                  {actionLoading === 'reset' ? 'Resetting...' : 'Set typed password'}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
